import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  HelpCircle, Search, ChevronDown, ChevronRight, Mail, MessageSquare,
  Book, Video, Dumbbell, Apple, Moon, Calendar, Trophy, Users,
  Settings, CreditCard, Shield, Smartphone, CheckCircle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const FAQ_CATEGORIES = [
  { id: 'getting-started', name: 'Getting Started', icon: Book, color: 'text-blue-400' },
  { id: 'training', name: 'Training & Workouts', icon: Dumbbell, color: 'text-aura-primary' },
  { id: 'nutrition', name: 'Nutrition', icon: Apple, color: 'text-green-400' },
  { id: 'recovery', name: 'Recovery', icon: Moon, color: 'text-purple-400' },
  { id: 'account', name: 'Account & Billing', icon: CreditCard, color: 'text-amber-400' },
  { id: 'supporters', name: 'For Parents/Coaches', icon: Users, color: 'text-pink-400' },
];

const FAQS = [
  // Getting Started
  {
    category: 'getting-started',
    question: 'How do I get started with Aura?',
    answer: 'Welcome to Aura! After creating your account, complete the onboarding questionnaire to set up your profile. This helps us personalize your workouts, nutrition targets, and recovery recommendations. You can access all features from your dashboard.'
  },
  {
    category: 'getting-started',
    question: 'What is an Aura Score?',
    answer: 'Your Aura Score reflects your overall athletic development progress. It increases as you complete workouts, log meals, track recovery, and maintain consistency. Higher scores unlock badges and show your commitment to becoming a better athlete.'
  },
  {
    category: 'getting-started',
    question: 'How do I join my team or organization?',
    answer: 'If your coach or organization uses Aura, they\'ll provide you with an invite code. Go to "Join Team" from your dashboard and enter the code. You\'ll then see team-specific workouts and be visible on team leaderboards.'
  },
  {
    category: 'getting-started',
    question: 'Can I use Aura on multiple devices?',
    answer: 'Yes! Aura works on any device with a web browser. Your data syncs automatically across devices. For the best mobile experience, add Aura to your home screen using the "Install App" option in your browser.'
  },
  
  // Training
  {
    category: 'training',
    question: 'How do AI workout recommendations work?',
    answer: 'Our AI considers your sport, skill level, equipment available, recent activity, recovery status, and goals to generate personalized workouts. The more you use Aura, the better the recommendations become.'
  },
  {
    category: 'training',
    question: 'What\'s the difference between Build and Maintain mode?',
    answer: 'Build mode (off-season) features higher intensity, more volume, and progressive overload for maximum gains. Maintain mode (in-season) focuses on moderate intensity, injury prevention, and keeping your performance sharp without overtraining.'
  },
  {
    category: 'training',
    question: 'How do I log external workouts?',
    answer: 'Go to External Workouts from your training page. You can log sessions with personal trainers, S&C coaches, team practices, and more. These count toward your weekly goals and earn Aura points.'
  },
  {
    category: 'training',
    question: 'What is the Training Matrix?',
    answer: 'The Training Matrix is a 4-week structured program with specific drills and weekly targets. Complete drills multiple times per week to hit your targets. Parents can verify completions for linked athletes.'
  },
  
  // Nutrition
  {
    category: 'nutrition',
    question: 'How are my nutrition targets calculated?',
    answer: 'We use the Mifflin-St Jeor formula with your age, weight, height, activity level, and goals to calculate your daily calories and macros. Targets automatically adjust based on your weight goals (gain muscle, lose fat, maintain, etc.).'
  },
  {
    category: 'nutrition',
    question: 'How do I log my meals?',
    answer: 'Go to Nutrition and tap "Fuel Up" to log a meal. Search for foods using our database, select items, adjust portions, and save. You can also use quick-add for frequently eaten foods.'
  },
  {
    category: 'nutrition',
    question: 'Can I track hydration?',
    answer: 'Yes! The Nutrition page includes hydration tracking. We calculate your daily water target based on your weight and activity level. Use quick-add buttons (+8oz, +16oz, +24oz) to log your intake throughout the day.'
  },
  {
    category: 'nutrition',
    question: 'How does the grocery list work?',
    answer: 'The grocery list is auto-generated from your meal plan. You can also add custom items. For families, supporters can view and manage the grocery list for their linked athletes.'
  },
  
  // Recovery
  {
    category: 'recovery',
    question: 'What factors affect my recovery score?',
    answer: 'Your recovery score is calculated from sleep hours, sleep quality, stress level, muscle soreness, and fatigue. Log your recovery daily for the most accurate recommendations on training intensity.'
  },
  {
    category: 'recovery',
    question: 'How much sleep should I get?',
    answer: 'For athletes, we recommend 8-10 hours for teens and 7-9 hours for adults. Quality matters too! Consistent sleep schedules and limiting screen time before bed improve recovery significantly.'
  },
  {
    category: 'recovery',
    question: 'What are Morning Stretch Flows?',
    answer: 'Morning Stretch Flows are guided recovery routines you can complete each day. They include dynamic stretches and mobility work. Completing items earns Aura points and improves your recovery.'
  },
  
  // Account & Billing
  {
    category: 'account',
    question: 'What plans are available?',
    answer: 'Individual plans are $3/month. Family plans start at $3 + $1 per additional athlete (max $6/month for up to 4 athletes). Organizations can choose between org-funded ($49/mo + $2/athlete) or athlete-funded ($3/athlete) models.'
  },
  {
    category: 'account',
    question: 'How do I update my profile information?',
    answer: 'Go to Settings from your dashboard. You can update your personal info, sport, position, equipment available, and training preferences. Changes take effect immediately for future workout recommendations.'
  },
  {
    category: 'account',
    question: 'How do I reset my password?',
    answer: 'Click "Forgot Password" on the login page and enter your email. We\'ll send you a reset link (make sure to check your spam folder). For security, reset links expire after 1 hour.'
  },
  {
    category: 'account',
    question: 'Can I cancel my subscription?',
    answer: 'Yes, you can cancel anytime from Settings > Subscription. You\'ll retain access until the end of your billing period. Your data is preserved if you decide to resubscribe later.'
  },
  
  // Supporters
  {
    category: 'supporters',
    question: 'How do I link to my athlete\'s account?',
    answer: 'From your Supporter dashboard, click "Add Athlete" and enter their email address. They\'ll receive a link request. Once approved, you\'ll see their full progress, nutrition, and recovery data.'
  },
  {
    category: 'supporters',
    question: 'What can I see as a supporter?',
    answer: 'Supporters have full visibility into linked athletes\' workouts, nutrition logs, recovery scores, hydration, goals, and Aura progress. You can also manage their schedule, verify workout completions, and view the grocery list.'
  },
  {
    category: 'supporters',
    question: 'Can I assign workouts to my athlete?',
    answer: 'Yes! With Coach Assignments (premium feature), you can create custom workouts with exercises, sets, and reps, then assign them to specific athletes with due dates. Track completion status from your dashboard.'
  },
  {
    category: 'supporters',
    question: 'How do workout verifications work?',
    answer: 'When your linked athlete completes a Training Matrix drill, you\'ll see it in your Pending Verifications. Review and approve/reject completions to ensure accountability. Athletes are notified of your decision.'
  },
];

const QUICK_LINKS = [
  { name: 'Video Tutorials', icon: Video, url: '#', coming: true },
  { name: 'Training Guide', icon: Book, url: '/training-program' },
  { name: 'Equipment Recs', icon: Dumbbell, url: '/equipment' },
  { name: 'Report an Issue', icon: MessageSquare, url: '/report-issue' },
];

const HelpCenterPage = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const filteredFaqs = FAQS.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleContactSubmit = async () => {
    if (!contactSubject.trim() || !contactMessage.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/support/contact`, {
        subject: contactSubject,
        message: contactMessage,
        user_email: user?.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Message sent! We\'ll get back to you within 24 hours.');
      setShowContactForm(false);
      setContactSubject('');
      setContactMessage('');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-aura-primary to-aura-secondary mb-4">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight">
            Help Center
          </h1>
          <p className="text-aura-text-muted mt-2">
            Find answers, guides, and support for your Aura journey
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-aura-text-muted" />
          <Input
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-6 bg-aura-surface border-white/10 text-white text-lg"
            data-testid="help-search"
          />
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {QUICK_LINKS.map((link) => (
            <a
              key={link.name}
              href={link.url}
              className={`aura-card p-4 flex flex-col items-center text-center hover:border-white/20 transition-all ${link.coming ? 'opacity-60' : ''}`}
              onClick={(e) => link.coming && e.preventDefault()}
            >
              <link.icon className="w-6 h-6 text-aura-primary mb-2" />
              <span className="text-sm text-white">{link.name}</span>
              {link.coming && (
                <Badge className="mt-1 bg-white/10 text-aura-text-muted text-xs border-0">Coming Soon</Badge>
              )}
            </a>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeCategory === 'all'
                ? 'bg-aura-primary text-white'
                : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
            }`}
            data-testid="category-all"
          >
            All Topics
          </button>
          {FAQ_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                activeCategory === cat.id
                  ? 'bg-aura-primary text-white'
                  : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
              }`}
              data-testid={`category-${cat.id}`}
            >
              <cat.icon className={`w-4 h-4 ${activeCategory === cat.id ? 'text-white' : cat.color}`} />
              {cat.name}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Frequently Asked Questions
              {searchQuery && (
                <span className="text-aura-text-muted font-normal text-sm ml-2">
                  ({filteredFaqs.length} results)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-8">
                <HelpCircle className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
                <p className="text-white font-medium">No results found</p>
                <p className="text-aura-text-muted text-sm mt-1">
                  Try a different search term or category
                </p>
              </div>
            ) : (
              filteredFaqs.map((faq, index) => {
                const category = FAQ_CATEGORIES.find(c => c.id === faq.category);
                const isExpanded = expandedFaq === index;
                
                return (
                  <div 
                    key={index}
                    className="border border-white/10 rounded-lg overflow-hidden"
                    data-testid={`faq-${index}`}
                  >
                    <button
                      onClick={() => setExpandedFaq(isExpanded ? null : index)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {category && (
                          <category.icon className={`w-5 h-5 ${category.color} flex-shrink-0`} />
                        )}
                        <span className="text-white font-medium">{faq.question}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-aura-text-muted flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-aura-text-muted flex-shrink-0" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="pl-8 text-aura-text-muted leading-relaxed">
                          {faq.answer}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Contact Support */}
        <Card className="bg-gradient-to-r from-aura-primary/10 to-aura-secondary/10 border-aura-primary/30">
          <CardContent className="p-6">
            {!showContactForm ? (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-5 h-5 text-aura-primary" />
                    <h3 className="font-heading font-bold text-white uppercase">Still Need Help?</h3>
                  </div>
                  <p className="text-aura-text-muted text-sm">
                    Can't find what you're looking for? Our support team is here to help.
                  </p>
                </div>
                <Button 
                  onClick={() => setShowContactForm(true)}
                  className="bg-aura-primary hover:bg-aura-primary-hover whitespace-nowrap"
                  data-testid="contact-support-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-white uppercase">Contact Support</h3>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowContactForm(false)}
                    className="text-aura-text-muted"
                  >
                    Cancel
                  </Button>
                </div>
                <Input
                  placeholder="Subject"
                  value={contactSubject}
                  onChange={(e) => setContactSubject(e.target.value)}
                  className="bg-aura-bg border-white/10 text-white"
                  data-testid="contact-subject"
                />
                <Textarea
                  placeholder="Describe your issue or question..."
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  rows={4}
                  className="bg-aura-bg border-white/10 text-white"
                  data-testid="contact-message"
                />
                <Button 
                  onClick={handleContactSubmit}
                  disabled={submitting}
                  className="w-full bg-aura-primary hover:bg-aura-primary-hover"
                  data-testid="send-message-btn"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support Info */}
        <div className="text-center text-aura-text-muted text-sm">
          <p>Support hours: Monday - Friday, 9am - 6pm EST</p>
          <p className="mt-1">Average response time: Within 24 hours</p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HelpCenterPage;
